/**
 * TWSE 即時股價 API 服務
 * 使用台股交易所 MIS API 取得即時股價資料
 * 使用統一的 CORS 代理和緩存服務
 */

import { logger } from "../utils/logger";
import { ErrorHandler } from "../utils/errorHandler";
import { RetryHelper } from "../utils/retryHelper";
import corsProxyService from "./corsProxyService";
import { realtimeCache } from "../utils/cacheService";
import staticStockDataService from "./staticStockDataService";

export interface TWSERealtimeStockData {
  tv: string; // 總成交量
  ps: string; // 昨收價
  pz: string; // 成交價
  a: string; // 最佳5檔賣價 (以_分隔)
  b: string; // 最佳5檔買價 (以_分隔)
  c: string; // 股票代號
  d: string; // 日期 (YYYYMMDD)
  ch: string; // 完整代號 (如: 2330.tw)
  tlong: string; // Unix timestamp
  f: string; // 最佳5檔賣量 (以_分隔)
  ip: string; // IP
  g: string; // 最佳5檔買量 (以_分隔)
  mt: string; // 市場類型
  h: string; // 最高價
  i: string; // 產業別代碼
  it: string; // 產業別子代碼
  l: string; // 最低價
  n: string; // 股票名稱
  o: string; // 開盤價
  p: string; // 暫停交易註記
  ex: string; // 交易所 (tse/otc)
  s: string; // 成交量
  t: string; // 成交時間 (HH:MM:SS)
  u: string; // 漲停價
  v: string; // 成交筆數
  w: string; // 跌停價
  nf: string; // 股票全名
  y: string; // 昨收價
  z: string; // 成交價 (最新價)
  ts: string; // 交易狀態
}

export interface TWSERealtimeApiResponse {
  msgArray: TWSERealtimeStockData[];
  referer: string;
  userDelay: number;
  rtcode: string;
  queryTime: {
    sysDate: string;
    stockInfoItem: number;
    stockInfo: number;
    sessionStr: string;
    sysTime: string;
    showChart: boolean;
    sessionFromTime: number;
    sessionLatestTime: number;
  };
  rtmessage: string;
}

export interface TWSEStockNameData {
  c: string; // 股票代號
  key: string; // 識別鍵
  n: string; // 股票名稱
}

export interface TWSEStockNameApiResponse {
  build: string;
  rtcode: string;
  datas: TWSEStockNameData[];
  rtmessage: string;
}

export interface StockNameInfo {
  symbol: string;
  name: string;
  key: string;
  market?: "上市" | "上櫃" | "興櫃"; // 新增市場類型
  fullName?: string; // 新增完整名稱
  industry?: string; // 新增產業類別
}

export interface RealtimeStockPrice {
  symbol: string;
  name: string;
  fullName: string;
  currentPrice: number;
  previousClose: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  volume: number;
  turnover: number;
  change: number;
  changePercent: number;
  updateTime: string;
  date: string;
  isMarketOpen: boolean;
  bidPrices: number[]; // 最佳5檔買價
  askPrices: number[]; // 最佳5檔賣價
  bidVolumes: number[]; // 最佳5檔買量
  askVolumes: number[]; // 最佳5檔賣量
  upperLimit: number; // 漲停價
  lowerLimit: number; // 跌停價
  totalVolume: number; // 總成交量
  transactionCount: number; // 成交筆數
}

class TWSERealtimeService {
  private readonly baseUrl =
    "https://mis.twse.com.tw/stock/api/getStockInfo.jsp";
  private readonly stockNamesUrl =
    "https://mis.twse.com.tw/stock/api/getStockNames.jsp";

  /**
   * 建構股票查詢參數
   */
  private buildStockQueryParams(symbols: string[]): string {
    return (
      symbols
        .map((symbol) => {
          const cleanSymbol = symbol.replace(".tw", "");
          const exchange = this.determineExchange(cleanSymbol);
          return `${exchange}_${cleanSymbol}.tw`;
        })
        .join("|") + "|"
    );
  }

  /**
   * 判斷股票屬於哪個交易所
   * 現在使用靜態資料來正確判斷上市/上櫃
   */
  private determineExchange(symbol: string): string {
    try {
      // 嘗試從靜態資料中查找股票資訊 (同步查詢)
      const stockInfo = staticStockDataService.getStockInfoSync(symbol);
      
      if (stockInfo) {
        // 有找到股票資訊，使用正確的市場類型
        if (stockInfo.market === "上櫃") {
          return "otc";
        } else if (stockInfo.market === "上市") {
          return "tse";
        }
      }
      
      // 如果沒有找到資料（可能是資料尚未載入），使用傳統的數字範圍判斷
      const num = parseInt(symbol);
      if (/^\d{4}$/.test(symbol) && num >= 1000 && num <= 9999) {
        // 基本的數字範圍判斷 (不是 100% 正確，但是合理的備用)
        if (num >= 6000) {
          return "otc"; // 上櫃
        } else {
          return "tse"; // 上市
        }
      }
      
      return "tse"; // 預設為上市
    } catch (error) {
      logger.api.debug("Failed to determine exchange for symbol", { symbol, error });
      return "tse"; // 預設為上市
    }
  }

  /**
   * 建構完整的 API URL
   */
  private buildApiUrl(symbols: string[]): string {
    const stockParams = this.buildStockQueryParams(symbols);
    const params = new URLSearchParams({
      json: "1",
      delay: "0",
      ex_ch: stockParams,
    });
    return `${this.baseUrl}?${params}`;
  }

  /**
   * 解析即時股價資料
   */
  private parseRealtimeStockData(
    data: TWSERealtimeStockData
  ): RealtimeStockPrice | null {
    try {
      // 輔助函數：安全解析數字，處理 "-" 字符
      const safeParseFloat = (value: string): number => {
        if (!value || value === "-" || value.trim() === "") {
          return 0;
        }
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      };

      // 安全解析整數
      const safeParseInt = (value: string): number => {
        if (!value || value === "-" || value.trim() === "") {
          return 0;
        }
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? 0 : parsed;
      };

      // 獲取現價和昨收價
      // 優先順序：z(當盤最新成交價) > b(買一價) > a(賣一價) > y(昨收價)
      // 參考：https://www.twse.com.tw/zh/trading/historical/bwibbu-d
      let currentPrice = 0;
      if (data.z && data.z !== "-") {
        currentPrice = safeParseFloat(data.z);
      } else if (data.pz && data.pz !== "-") {
        currentPrice = safeParseFloat(data.pz);
      } else if (data.b && data.b !== "-") {
        const bidPrices = this.parseCommaSeparatedString(data.b);
        currentPrice = bidPrices.length > 0 ? bidPrices[0] : 0;
      } else if (data.a && data.a !== "-") {
        const askPrices = this.parseCommaSeparatedString(data.a);
        currentPrice = askPrices.length > 0 ? askPrices[0] : 0;
      }
      
      const previousClose = safeParseFloat(data.y) || safeParseFloat(data.ps);

      // 如果沒有有效的現價，代表可能沒有成交或暫停交易，返回 null
      if (currentPrice === 0) {
        logger.api.debug("No valid current price found, skipping stock", {
          symbol: data.c,
          z: data.z,
          pz: data.pz,
          b: data.b,
          a: data.a,
          y: data.y,
          ps: data.ps
        });
        return null;
      }

      const change = currentPrice - previousClose;
      const changePercent =
        previousClose > 0 ? (change / previousClose) * 100 : 0;

      const bidPrices = this.parseCommaSeparatedString(data.b);
      const askPrices = this.parseCommaSeparatedString(data.a);
      const bidVolumes = this.parseCommaSeparatedString(data.g);
      const askVolumes = this.parseCommaSeparatedString(data.f);

      return {
        symbol: data.c,
        name: data.n || "",
        fullName: data.nf || data.n || "",
        currentPrice,
        previousClose,
        openPrice: safeParseFloat(data.o),
        highPrice: safeParseFloat(data.h),
        lowPrice: safeParseFloat(data.l),
        volume: safeParseInt(data.s),
        turnover: safeParseInt(data.tv),
        change,
        changePercent,
        updateTime: data.t || "",
        date: data.d || "",
        isMarketOpen: this.isMarketTime(data.t),
        bidPrices,
        askPrices,
        bidVolumes,
        askVolumes,
        upperLimit: safeParseFloat(data.u),
        lowerLimit: safeParseFloat(data.w),
        totalVolume: safeParseInt(data.tv),
        transactionCount: safeParseInt(data.v),
      };
    } catch (error) {
      logger.api.error("Error parsing realtime stock data", { error, data });
      return null;
    }
  }

  /**
   * 解析以底線分隔的字串為數字陣列
   */
  private parseCommaSeparatedString(str: string): number[] {
    if (!str) return [];

    return str
      .split("_")
      .filter((item) => item.trim() !== "")
      .map((item) => parseFloat(item))
      .filter((num) => !isNaN(num));
  }

  /**
   * 判斷是否為交易時間
   */
  private isMarketTime(timeStr: string): boolean {
    if (!timeStr) return false;

    try {
      const [hours, minutes] = timeStr.split(":").map(Number);
      const timeMinutes = hours * 60 + minutes;

      const marketOpenMinutes = 9 * 60; // 09:00
      const marketCloseMinutes = 13 * 60 + 30; // 13:30

      return (
        timeMinutes >= marketOpenMinutes && timeMinutes <= marketCloseMinutes
      );
    } catch {
      return false;
    }
  }

  /**
   * 使用統一的 CORS 代理服務獲取資料
   */
  private async fetchWithCorsProxy(
    symbols: string[]
  ): Promise<TWSERealtimeApiResponse> {
    try {
      const apiUrl = this.buildApiUrl(symbols);
      logger.api.info("Fetching realtime data via CORS proxy", {
        apiUrl,
        symbols,
      });

      const response = await corsProxyService.fetch(apiUrl);
      const data: TWSERealtimeApiResponse = await response.json();

      if (data.rtcode !== "0000") {
        throw new Error(`TWSE API error: ${data.rtmessage}`);
      }

      return data;
    } catch (error) {
      const appError = ErrorHandler.handleError(
        error,
        "TWSERealtimeService.fetchWithCorsProxy"
      );
      logger.api.error("Failed to fetch realtime data", appError);
      throw appError;
    }
  }

  /**
   * 獲取股票名稱資訊 (優化版，使用本地快取)
   * @param symbolPrefix 股票代號前綴 (可以是部分代號)
   */
  async getStockNames(symbolPrefix: string): Promise<StockNameInfo[]> {
    try {
      logger.api.info("Fetching stock names via static data service", {
        symbolPrefix,
      });

      // 優先使用靜態資料服務 (無需 API 呼叫！)
      const staticResults = await staticStockDataService.getStockNames(
        symbolPrefix
      );

      // 轉換為相容格式，並加入額外資訊
      const results: StockNameInfo[] = [];

      for (const item of staticResults) {
        // 獲取完整股票資訊
        const stockInfo = await staticStockDataService.getStockInfo(
          item.symbol
        );

        results.push({
          symbol: item.symbol,
          name: item.name,
          key: item.key,
          market: stockInfo?.market,
          fullName: stockInfo?.fullName,
          industry: stockInfo?.industry,
        });
      }

      logger.api.info("Successfully fetched stock names (static data)", {
        symbolPrefix,
        resultCount: results.length,
        source: "static_json",
      });

      return results;
    } catch (error) {
      logger.api.warn(
        "Static stock data failed, falling back to optimized service",
        { symbolPrefix, error }
      );

      // 如果靜態資料失敗，回退到優化服務
      return this.getStockNamesOptimized(symbolPrefix);
    }
  }

  /**
   * 優化的股票名稱獲取方法 (備用)
   * @param symbolPrefix 股票代號前綴
   */
  private async getStockNamesOptimized(
    symbolPrefix: string
  ): Promise<StockNameInfo[]> {
    try {
      logger.api.info("獲取股票名稱透過優化服務", {
        symbolPrefix,
      });

      // 優化服務已移除，直接回傳空陣列
      logger.api.warn("優化服務不可用，直接回傳空結果", {
        symbolPrefix,
      });
      return [];
    } catch (error) {
      logger.api.warn(
        "優化股票名稱失敗，回退到原始 API",
        { symbolPrefix, error }
      );

      // 如果優化服務失敗，回退到原始 API
      return this.getStockNamesLegacy(symbolPrefix);
    }
  }

  /**
   * 原始的股票名稱獲取方法 (備用)
   * @param symbolPrefix 股票代號前綴
   */
  private async getStockNamesLegacy(
    symbolPrefix: string
  ): Promise<StockNameInfo[]> {
    try {
      const timestamp = Date.now();
      const params = new URLSearchParams({
        n: symbolPrefix,
        _: timestamp.toString(),
      });
      const apiUrl = `${this.stockNamesUrl}?${params}`;

      logger.api.info("Fetching stock names (legacy)", {
        symbolPrefix,
        apiUrl,
      });

      const response = await corsProxyService.fetch(apiUrl);
      const data: TWSEStockNameApiResponse = await response.json();

      if (data.rtcode !== "0000") {
        throw new Error(`TWSE Stock Names API error: ${data.rtmessage}`);
      }

      const results: StockNameInfo[] = data.datas.map((item) => ({
        symbol: item.c,
        name: item.n,
        key: item.key,
      }));

      logger.api.info("Successfully fetched stock names (legacy)", {
        symbolPrefix,
        resultCount: results.length,
      });

      return results;
    } catch (error) {
      logger.api.error("Error fetching stock names (legacy)", {
        symbolPrefix,
        error,
      });
      return [];
    }
  }

  /**
   * 批量獲取股票名稱 (絶對優化版，優先使用靜態資料)
   * @param symbols 完整的股票代號列表
   */
  async getBatchStockNames(symbols: string[]): Promise<Map<string, string>> {
    try {
      logger.api.info("Fetching batch stock names via static data service", {
        symbolCount: symbols.length,
      });

      // 優先使用靜態資料服務，無需 API 呼叫！
      const nameMap = await staticStockDataService.getBatchStockNames(
        symbols
      );

      logger.api.info("Batch stock names fetch completed (static data)", {
        requestedCount: symbols.length,
        resolvedCount: nameMap.size,
        coverage: `${((nameMap.size / symbols.length) * 100).toFixed(1)}%`,
        additionalApiCalls: 0, // 絕對無需 API 呼叫！
      });

      return nameMap;
    } catch (error) {
      logger.api.warn(
        "Static batch stock names failed, falling back to optimized service",
        { error }
      );

      // 回退到優化服務
      return this.getBatchStockNamesOptimized(symbols);
    }
  }

  /**
   * 優化的批量股票名稱獲取方法 (備用)
   * @param symbols 完整的股票代號列表
   */
  private async getBatchStockNamesOptimized(
    symbols: string[]
  ): Promise<Map<string, string>> {
    try {
      logger.api.info("獲取批量股票名稱透過優化服務", {
        symbolCount: symbols.length,
      });

      // 優化服務已移除，直接回傳空 Map
      logger.api.warn("優化服務不可用，直接回傳空 Map", {
        symbols,
      });
      return new Map<string, string>();
    } catch (error) {
      logger.api.warn(
        "優化批量股票名稱失敗，回退到原始方法",
        { error }
      );

      // 回退到原始方法
      return this.getBatchStockNamesLegacy(symbols);
    }
  }

  /**
   * 原始的批量股票名稱獲取方法 (備用)
   * @param symbols 完整的股票代號列表
   */
  private async getBatchStockNamesLegacy(
    symbols: string[]
  ): Promise<Map<string, string>> {
    const nameMap = new Map<string, string>();

    // 按前兩碼分組來減少 API 呼叫
    const prefixGroups = new Map<string, string[]>();

    symbols.forEach((symbol) => {
      const prefix = symbol.substring(0, 2);
      if (!prefixGroups.has(prefix)) {
        prefixGroups.set(prefix, []);
      }
      prefixGroups.get(prefix)!.push(symbol);
    });

    logger.api.info("Using legacy batch stock names method", {
      totalSymbols: symbols.length,
      prefixCount: prefixGroups.size,
      prefixes: Array.from(prefixGroups.keys()),
    });

    // 逐個前綴查詢
    for (const [prefix, prefixSymbols] of Array.from(prefixGroups.entries())) {
      try {
        const nameInfos = await this.getStockNamesLegacy(prefix);

        // 將結果對應到實際的股票代號
        nameInfos.forEach((info) => {
          if (prefixSymbols.includes(info.symbol)) {
            nameMap.set(info.symbol, info.name);
          }
        });

        // 避免請求過於頻繁
        if (prefixGroups.size > 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        logger.api.warn("Failed to fetch names for prefix (legacy)", {
          prefix,
          error,
        });
      }
    }

    logger.api.info("Legacy batch stock names fetch completed", {
      requestedCount: symbols.length,
      resolvedCount: nameMap.size,
      coverage: `${((nameMap.size / symbols.length) * 100).toFixed(1)}%`,
    });

    return nameMap;
  }

  /**
   * 獲取單支股票即時價格
   */
  async getStockPrice(symbol: string): Promise<RealtimeStockPrice | null> {
    try {
      const results = await this.getStockPrices([symbol]);
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      logger.api.error("Error getting stock price", { symbol, error });
      return null;
    }
  }

  /**
   * 獲取多支股票即時價格
   */
  async getStockPrices(symbols: string[]): Promise<RealtimeStockPrice[]> {
    if (symbols.length === 0) {
      return [];
    }

    // 檢查快取
    const cacheKey = [...symbols].sort().join(","); // DATA-08:複製後排序,不變動呼叫端陣列
    const cached = realtimeCache.get(cacheKey) as RealtimeStockPrice[] | null;
    if (cached) {
      logger.api.info("Retrieved realtime prices from cache", {
        symbolCount: symbols.length,
      });
      return cached;
    }

    try {
      logger.api.info("Fetching realtime prices", {
        symbolCount: symbols.length,
      });

      // 使用指數退避重試機制，網路不穩時自動重試
      // 注意：corsProxyService 內部已有 5 層代理瀑布與斷路器，此處僅重試 1 次以避免阻塞過久
      const apiResponse = await RetryHelper.apiRetry(
        () => this.fetchWithCorsProxy(symbols),
        {
          maxRetries: 1,
          baseDelay: 2000,
          maxDelay: 10000,
        }
      );

      if (!apiResponse.msgArray) {
        throw new Error("API 回應格式錯誤：缺少 msgArray");
      }

      const results: RealtimeStockPrice[] = [];

      for (const stockData of apiResponse.msgArray) {
        const parsed = this.parseRealtimeStockData(stockData);
        if (parsed) {
          results.push(parsed);
        }
      }

      // 更新快取
      realtimeCache.set(cacheKey, results);
      logger.api.info("Cached realtime prices", {
        resultCount: results.length,
      });

      return results;
    } catch (error) {
      logger.api.error("Error fetching realtime stock prices", {
        symbols,
        error,
      });
      return [];
    }
  }

  /**
   * 批量獲取股價 (自動分批處理)
   */
  async getBatchStockPrices(
    symbols: string[],
    batchSize = 50
  ): Promise<RealtimeStockPrice[]> {
    const results: RealtimeStockPrice[] = [];

    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize);

      try {
        const batchResults = await this.getStockPrices(batch);
        results.push(...batchResults);

        // 避免請求過於頻繁，添加小延遲
        if (i + batchSize < symbols.length) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (error) {
        logger.api.error("Error fetching batch", {
          batchIndex: Math.floor(i / batchSize) + 1,
          error,
        });
        // 繼續處理下一批
      }
    }

    return results;
  }

  /**
   * 檢查服務狀態
   */
  async checkServiceStatus(): Promise<boolean> {
    try {
      const testResult = await this.getStockPrice("2330"); // 使用台積電測試
      return testResult !== null;
    } catch (error) {
      logger.api.error("Service status check failed", error);
      return false;
    }
  }

  /**
   * 清除快取
   */
  clearCache(): void {
    realtimeCache.clear();
    logger.api.info("Realtime cache cleared");
  }

  /**
   * 重設連接偏好
   */
  resetConnectionPreference(): void {
    corsProxyService.resetConnectionPreference();
    logger.api.info("CORS proxy connection preference reset");
  }

  /**
   * 取得服務狀態資訊
   */
  getServiceStatus() {
    const corsStatus = corsProxyService.getServiceStatus();
    const cacheStats = realtimeCache.getStats();

    return {
      corsProxy: corsStatus,
      cache: cacheStats,
    };
  }

  /**
   * 強制更新股票名稱快取 (已移除)
   */
  async forceUpdateStockNameCache(): Promise<void> {
    logger.api.warn("優化股票名稱服務已移除，無法更新快取");
  }

  /**
   * 清除股票名稱快取 (已移除)
   */
  clearStockNameCache(): void {
    logger.api.warn("優化股票名稱服務已移除，無法清除快取");
  }

  /**
   * 根據股票代號獲取完整股票資訊 (已移除)
   */
  async getStockInfo(symbol: string): Promise<any | null> {
    logger.api.warn("優化股票名稱服務已移除，無法獲取股票資訊", { symbol });
    return null;
  }

  /**
   * 根據市場類型獲取股票列表 (已移除)
   */
  async getStocksByMarket(
    market: "上市" | "上櫃"
  ): Promise<any[]> {
    logger.api.warn("優化股票名稱服務已移除，無法獲取市場股票", { market });
    return [];
  }

  /**
   * 根據股票名稱查找代號 (已移除)
   */
  async findSymbolByName(name: string): Promise<string | null> {
    logger.api.warn("優化股票名稱服務已移除，無法查找股票代號", { name });
    return null;
  }
}

// 導出單例
export const twseRealtimeService = new TWSERealtimeService();
export default twseRealtimeService;
